import React from "react"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { Box, CircularProgress, Divider, Grid, ListItemButton, Typography } from "@mui/material"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { fetchMerchantSwift, FetchMerchantSwiftResponse, MerchantAccountQuery } from "../merchantAccountApi"
import { useMerchant } from "../../../../../merchants/merchantHooks"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faLocationDot, faChevronRight } from "@fortawesome/pro-solid-svg-icons"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { makeStyles } from "tss-react/mui"
import { Suspense } from "react"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { useLocation, useNavigate } from "react-router-dom"
import { MERCHANT_REPORTS_ROUTE } from "../../../../../../constants/routes"
import { pushContext } from "../../../../../context/contextSlice"
import { useAppDispatch } from "../../../../../store/storeHooks"
import { SwiftMerchant } from "../merchantAccountTypes"

type SwiftMerchantDialogProps = {
    open: boolean
    onClose: () => void
}

const useStyles = makeStyles()((theme) => ({
    content: {
        minHeight: "20rem",
    },
    title: {
        fontSize: "3rem",
        fontWeight: 600,
    },
    subtitle: {
        color: theme.palette.tailwind.neutral[600],
    },
    merchantItem: {
        padding: "1rem",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
    },
    merchantIcon: {
        padding: "0.2rem 1rem",
        color: theme.palette.tailwind.neutral[600],
    },
    emptyContent: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        paddingTop: "5rem",
    },
}))

export const SwiftMerchantDialog = ({ open, onClose }: SwiftMerchantDialogProps) => {
    const { classes } = useStyles()

    const navigate = useNavigate()
    const { pathname } = useLocation()
    const dispatch = useAppDispatch()

    const onMerchantClick = (merchant: SwiftMerchant) => {
        dispatch(
            pushContext({
                type: "merchant",
                merchant: {
                    ...merchant,
                    caseNumber: merchant.caseNumber ?? "",
                    addressDisplayText: merchant.address.displayText,
                },
                returnUrl: pathname,
            }),
        )
        onClose()

        navigate(MERCHANT_REPORTS_ROUTE)
    }

    return (
        <StandardDialog
            open={open}
            onClose={onClose}
            maxWidthProp="md"
            dialogTitle={
                <LocalizedStrict id="merchant-account-swift-dialog-title">
                    <>Swift merchant</>
                </LocalizedStrict>
            }
            subtitle={
                <LocalizedStrict id="merchant-account-swift-dialog-subtitle">
                    <>You have access to the following merchant. Select one to swift.</>
                </LocalizedStrict>
            }
        >
            <QueryErrorBoundary>
                <Suspense
                    fallback={
                        <Grid container alignItems="center" justifyContent="center" className={classes.content}>
                            <CircularProgress size={70} />
                        </Grid>
                    }
                >
                    <SwiftMerchantContent onMerchantClick={onMerchantClick} />
                </Suspense>
            </QueryErrorBoundary>
        </StandardDialog>
    )
}

const transformData = (data: FetchMerchantSwiftResponse) => data.merchants ?? []

function SwiftMerchantContent({ onMerchantClick }: { onMerchantClick: (merchant: SwiftMerchant) => void }) {
    const { merchantId } = useMerchant()
    const { classes } = useStyles()

    const { data: merchantSwift } = useApiQuery(fetchMerchantSwift, {
        queryName: MerchantAccountQuery.SWIFT,
        dependencies: {
            merchantId,
        },
        suspense: true,
        useErrorBoundary: true,
        enabled: Boolean(merchantId),
        select: transformData,
    })

    return (
        <Box className={classes.content}>
            <Divider />
            {merchantSwift?.map((merchant) => (
                <React.Fragment key={merchant.id}>
                    <ListItemButton onClick={() => onMerchantClick(merchant)} className={classes.merchantItem}>
                        <Box display="flex">
                            <FontAwesomeIcon className={classes.merchantIcon} size="lg" icon={faLocationDot} />
                            <Box>
                                <Typography variant="subtitle1">
                                    {`${merchant?.caseNumber ? `${merchant.caseNumber}, ` : ""}${merchant.name}`}
                                </Typography>
                                <Typography variant="body2">{merchant.address.displayText}</Typography>
                            </Box>
                        </Box>
                        <Box>
                            <FontAwesomeIcon className={classes.merchantIcon} icon={faChevronRight} />
                        </Box>
                    </ListItemButton>
                    <Divider />
                </React.Fragment>
            ))}
            {merchantSwift?.length === 0 && (
                <Box className={classes.emptyContent}>
                    <FontAwesomeIcon className={classes.merchantIcon} size="lg" icon={faLocationDot} />
                    <LocalizedStrict id="merchant-account-swift-dialog-empty-title">
                        <Typography>You don't have access to other merchants.</Typography>
                    </LocalizedStrict>
                </Box>
            )}
        </Box>
    )
}
